/**
 * ./src/hooks/useIsMobile.ts
 *
 * hook to detect if the device is mobile, with SSR-safe hydration
 * Returns false during SSR and initial render to prevent hydration mismatches,
 * then updates to actual device detection before browser paint using useLayoutEffect.
 * assumes default for mobile of < 1044 (BREAKPOINT_MD)
 *
 * @copyright DesignShop, 2024
 * @since 29/04/2024
 */
import { useLayoutEffect, useState } from 'react';

import { BREAKPOINT_MD } from '@/modules/common/constants';

import useDeviceDetectClient, { ScreenSizeBreakpoint } from './useDeviceDetectClient';

const useIsMobile = (
  maxScreenSizeBreakpoint: number | ScreenSizeBreakpoint | string = BREAKPOINT_MD
): boolean => {
  const [isHydrated, setIsHydrated] = useState(false);
  const { isMobile } = useDeviceDetectClient(maxScreenSizeBreakpoint);

  useLayoutEffect(() => {
    setIsHydrated(true);
  }, []);

  // Return false during SSR and initial render to match server output
  // After hydration, return actual device detection (before browser paint)
  if (!isHydrated) {
    return false;
  }

  return isMobile;
};

export default useIsMobile;
